import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs';
import { RestApiBaseService } from '../../http/rest-api-base.service';
import type { ApiResponse } from '../../models/api-response.model';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface IPermission {
  _id?: string;
  module?: string;
  feature?: string;
  action?: string;
  allowed?: boolean;
}

export interface IMe {
  _id?: string;
  full_name?: string;
  email?: string;
  business_id?: string;
  [key: string]: unknown;
}

export interface IPermissionFeature {
  feature?: string;
  permission?: string | null;
  [key: string]: unknown;
}

export interface IPermissionModule {
  module?: string;
  features?: IPermissionFeature[];
  [key: string]: unknown;
}

export interface IGroupedPermissions {
  POSPermissionResult?: IPermissionModule[];
  adminPermissionResult?: IPermissionModule[];
  [key: string]: unknown;
}

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class MeRestApiService extends RestApiBaseService {

  private readonly http = inject(HttpClient);

  private get meBase() { return `${this.v2Base}/me`; }
  private get employeesBase() { return `${this.v2Base}/employees`; }

  // ── GET /api/v2/employees/me ──────────────────────────────────────────────
  // GQL: getLoggedInUserDetail — all params optional
  getLoggedInUserDetail(params: Record<string, string> = {}) {
    return this.http.get<ApiResponse<IMe>>(`${this.employeesBase}/me`, { params })
      .pipe(map(res => res.data ?? null), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/allowed-modules ───────────────────────────────────────
  getAllowedModules() {
    return this.http.get<ApiResponse<IPermission[]>>(`${this.meBase}/allowed-modules`)
      .pipe(map(res => res.data ?? []), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/modules/:module/permissions ────────────────────────────
  getModulePermissions(module: string) {
    return this.http.get<ApiResponse<IPermission[]>>(`${this.meBase}/modules/${module}/permissions`)
      .pipe(map(res => res.data ?? []), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/permissions[?modules[]=X] ──────────────────────────────
  // Without modules[] → getLoggedInUserAllowModulesAndFeature
  // With modules[]   → getLoggedInUserAllowModulesAndFeatureWithName
  getPermissions(modules?: string[]) {
    let params = new HttpParams();
    (modules ?? []).forEach(m => { params = params.append('modules[]', m); });
    return this.http.get<ApiResponse<unknown>>(`${this.meBase}/permissions`, { params })
      .pipe(map(res => res.data ?? null), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/permission-entries:modules ────────────────────────────
  getAllowedModuleEntries() {
    return this.http.get<ApiResponse<unknown[]>>(`${this.meBase}/permission-entries:modules`)
      .pipe(map(res => res.data ?? []), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/permission-entries ────────────────────────────────────
  getAllPermissionEntries() {
    return this.http.get<ApiResponse<unknown[]>>(`${this.meBase}/permission-entries`)
      .pipe(map(res => res.data ?? []), catchError(err => this.handleError(err)));
  }

  // ── GET /api/v2/me/permissions:grouped ───────────────────────────────────
  getGroupedPermissions() {
    return this.http.get<ApiResponse<IGroupedPermissions>>(`${this.meBase}/permissions:grouped`)
      .pipe(map(res => res.data ?? null), catchError(err => this.handleError(err)));
  }

  // ── POST /api/v2/employees:assignRole ────────────────────────────────────
  // body: { role: string, employees: string[] }
  assignRoleToEmployee(body: Record<string, unknown>) {
    return this.http.post<ApiResponse<boolean>>(`${this.employeesBase}:assignRole`, body)
      .pipe(map(res => res.data ?? false), catchError(err => this.handleError(err)));
  }
}
